import React, { useState, useEffect, useReducer, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ApprovalTypeLevelGeneralDataComponent from './ApprovalTypeLevelGeneralDataComponent';
import ApprovalTypeLevelEmployeeListComponent from './employees/ApprovalTypeLevelEmployeeListComponent';
import ApprovalTypeLevelUserGroupListComponent from './userGroups/ApprovalTypeLevelUserGroupListComponent';
import { FakeText, Card } from '@totalsoft/rocket-ui';
import { reducer, initialState } from '../../../reducers/approvalTypeLevel/containerReducer';
import { USER_GROUPS_QUERY } from 'features/common/components/userGroup/userGroupList/queries';
import { useValidation } from '@totalsoft/pure-validations-react';
import { approvalTypeLevelValidator } from 'features/approvalTypes/validators';
import { isValid } from '@totalsoft/pure-validations-react';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { APPROVAL_TYPE_LEVEL_QUERY, EMPLOYEES_QUERY } from './queries';
import { ADD_UPDATE_APPROVAL_TYPE_LEVEL } from './mutations';
import { APPROVAL_TYPE_LEVEL_UPDATED } from './subscriptions';
import { useToast } from 'hooks/toasts/useToast';

const ApprovalTypeLevelContainer = ({ approvalTypeId, approvalTypeLevelId, siteId, onSaveFnChanged, onRedirect }) => {
  const [t] = useTranslation();
  const addToast = useToast();

  const [state, dispatch] = useReducer(reducer, initialState);
  const [rendered, setRendered] = useState(false);
  const [approvalTypeLevelValidation, validateApprovalTypeLevel] = useValidation(approvalTypeLevelValidator);

  const { data, loading } = useQuery(APPROVAL_TYPE_LEVEL_QUERY, {
    variables: { approvalTypeId, siteId, approvalTypeLevelId, approvalTypeLevelSiteId: siteId },
    skip: approvalTypeId === undefined || siteId === undefined || approvalTypeLevelId === undefined
  });

  const { data: employeesData } = useQuery(EMPLOYEES_QUERY);

  const { data: userGroupsData } = useQuery(USER_GROUPS_QUERY);

  const [addOrUpdateApprovalTypeLevel] = useMutation(ADD_UPDATE_APPROVAL_TYPE_LEVEL);

  useSubscription(APPROVAL_TYPE_LEVEL_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('ApprovalTypes.ApprovalTypeLevelUpdated'), 'success');
      if (approvalTypeLevelId === undefined)
        onRedirect(
          data.approvalTypeLevelUpdated.approvalTypeId,
          data.approvalTypeLevelUpdated.approvalTypeLevelId,
          data.approvalTypeLevelUpdated.siteId
        );
      client.cache.evict({ id: `ApprovalTypeItem:{"approvalTypeId":${data.approvalTypeLevelUpdated.approvalTypeId}}` });
      client.cache.evict({ id: `ApprovalTypeLevelItem:{"approvalTypeLevelId":${data.approvalTypeLevelUpdated.approvalTypeLevelId}}` });
      client.cache.gc();
    }
  });

  useEffect(() => {
    setRendered(true);
  }, [rendered]);

  useEffect(() => {
    if (!approvalTypeLevelId) dispatch({ type: 'onApprovalTypeLevelAdded', approvalTypeId: approvalTypeId, siteId: siteId });
    else if (data && data.approvalTypeLevel) dispatch({ type: 'OnInitState', approvalTypeLevel: data.approvalTypeLevel });
  }, [approvalTypeId, approvalTypeLevelId, data, siteId]);

  const onSave = useCallback(() => {
    // eslint-disable-next-line no-unused-vars
    const { changed, ...approvalTypeLevelHeader } = state.header;

    const approvalTypeEmployees = [...state.deletedItems.approvalTypeEmployees];
    const approvalTypeUserGroups = [...state.deletedItems.approvalTypeUserGroups];
    const approvalTypeLevel = {
      ...approvalTypeLevelHeader,
      approvalTypeEmployees: [...state.approvalTypeEmployees],
      approvalTypeUserGroups: [...state.approvalTypeUserGroups]
    };

    const employeeIds = approvalTypeEmployees.map(item => {
      return item.approvalTypeEmployeeId;
    });

    const userGroupIds = approvalTypeUserGroups.map(item => {
      return item.approvalTypeUserGroupId;
    });

    const deletedItems = {
      approvalTypeEmployees: [...employeeIds],
      approvalTypeUserGroups: [...userGroupIds]
    };

    dispatch({ type: 'OnApprovalTypeLevelSaved' });
    addOrUpdateApprovalTypeLevel({
      variables: { input: { deletedItems, approvalTypeLevel } }
    });
  }, [addOrUpdateApprovalTypeLevel, state]);

  useEffect(() => {
    if (!rendered) return;
    onSaveFnChanged(onSave, !state.header.changed || !(approvalTypeLevelValidation |> isValid));
  }, [onSave, state.header.changed, rendered, onSaveFnChanged, approvalTypeLevelValidation]);

  useEffect(() => {
    validateApprovalTypeLevel(state);
  }, [state, validateApprovalTypeLevel]);

  return !loading ? (
    <>
      <Prompt when={state.header.changed} message={t('ApprovalTypes.ApprovalTypeLevel.LeavingWithoutSavingApprovalTypeLevel')} />
      <ApprovalTypeLevelGeneralDataComponent
        approvalTypeLevelItem={state.header}
        dispatch={dispatch}
        validation={approvalTypeLevelValidation.header}
      />
      <ApprovalTypeLevelEmployeeListComponent
        approvalTypeLevelEmployees={state.approvalTypeEmployees}
        dispatch={dispatch}
        employees={employeesData?.employees}
        validation={approvalTypeLevelValidation.approvalTypeEmployees}
      />
      <ApprovalTypeLevelUserGroupListComponent
        approvalTypeLevelUserGroups={state.approvalTypeUserGroups}
        dispatch={dispatch}
        userGroups={userGroupsData?.userGroupsConfig}
        validation={approvalTypeLevelValidation.approvalTypeUserGroups}
      />
    </>
  ) : (
    <Card>
      <FakeText lines={10} />
    </Card>
  );
};

ApprovalTypeLevelContainer.propTypes = {
  approvalTypeId: PropTypes.number.isRequired,
  approvalTypeLevelId: PropTypes.number,
  siteId: PropTypes.number.isRequired,
  onSaveFnChanged: PropTypes.func.isRequired,
  onRedirect: PropTypes.func.isRequired
};
export default ApprovalTypeLevelContainer;
